import { resolveBlockedRuleId } from "./rad-target-rules.js";
import { createDebugLogger, isDebugFlagEnabled } from "./debug.js";
import {
  buildTargetIndex,
  classifyBlockedReason,
  collectCandidateTargetIds,
  normalizeTargetSource,
  resolveConnectionScope,
  resolveSourceRoleId,
  sortUniqueIds,
  sourceExists,
} from "./rad-target-source-normalization.js";
import {
  getStateTargetDetails,
  resolveTargetQuery,
} from "./rad-target-evaluators.js";

const debugTargets = createDebugLogger("RAD_DEBUG_TARGETS");

export const queryTargetConnections = (rad) => (sourceInput) => {
  const source = normalizeTargetSource(sourceInput);
  if (!source.id || !source.class) {
    return {
      source,
      allowedIds: [],
      blocked: [],
      warnings: ["invalid_source"],
    };
  }

  const warnings = [];
  if (!sourceExists(rad, source)) {
    warnings.push("source_not_found");
  }

  const index = buildTargetIndex(rad);
  const resolved = resolveTargetQuery(rad, source);
  const allowedIds = sortUniqueIds(resolved.allowedIds);
  const exclusionReasons = resolved.exclusionReasons ?? {};
  warnings.push(...(resolved.warnings ?? []));
  const allowedSet = new Set(allowedIds);
  const sourceRoleId = resolveSourceRoleId(rad, source);
  const connectionScope = resolveConnectionScope(rad, source);
  const stateDetails = source.class === "state" ? getStateTargetDetails(rad, source) : null;

  const blocked = collectCandidateTargetIds(rad)
    .filter((id) => !allowedSet.has(id))
    .map((id) => {
      const reason =
        exclusionReasons[id] ??
        classifyBlockedReason(
          rad,
          index,
          source,
          id,
          sourceRoleId,
          connectionScope,
          stateDetails,
        );
      return {
        id,
        reason,
        ruleId: resolveBlockedRuleId(reason),
      };
    });

  const browserTraceFlag =
    typeof window !== "undefined" && window.__RAD_TARGET_TRACE__ === true;
  const traceEnabled = isDebugFlagEnabled("RAD_DEBUG_TARGETS") || browserTraceFlag;
  const trace = traceEnabled
    ? {
        source,
        sourceRoleId,
        connectionScope: connectionScope
          ? {
              interactionIds: [...connectionScope.interactionIds].sort(),
              roleIds: [...connectionScope.roleIds].sort(),
            }
          : null,
        resolverTrace: resolved.trace ?? null,
        exclusionReasons,
      }
    : undefined;
  if (traceEnabled) {
    debugTargets("queryTargetConnections trace", {
      ...trace,
      allowedIds,
      blocked,
      warnings: [...new Set(warnings)],
    });
  }

  return {
    source,
    allowedIds,
    blocked,
    warnings: [...new Set(warnings)],
    ...(traceEnabled ? { trace } : {}),
  };
};

export const getTargetConnections = (rad) => (source) =>
  queryTargetConnections(rad)(source).allowedIds;
